/**
 * 动画预设：JSON 只存 preset 名，运行时把它映射为 WAAPI keyframes。
 * 新增预设只改这里，编辑器的动画下拉与运行时自动同步。
 */
/**
 * 与 WAAPI 的 Keyframe 结构兼容，但不依赖 DOM lib，保证 Node 端消费方可用。
 * 运行时可直接把它传给 element.animate()。
 */
export interface PresetKeyframe {
  offset?: number;
  easing?: string;
  opacity?: number;
  transform?: string;
  [property: string]: string | number | null | undefined;
}

export interface AnimationPreset {
  name: string;
  label: string;
  group: 'in' | 'emphasis' | 'out';
  keyframes: PresetKeyframe[];
}

export const ANIMATION_PRESETS: Record<string, AnimationPreset> = {
  fadeIn: { name: 'fadeIn', label: '淡入', group: 'in', keyframes: [{ opacity: 0 }, { opacity: 1 }] },
  slideInUp: { name: 'slideInUp', label: '上滑进入', group: 'in', keyframes: [{ opacity: 0, transform: 'translateY(60px)' }, { opacity: 1, transform: 'translateY(0)' }] },
  slideInDown: { name: 'slideInDown', label: '下滑进入', group: 'in', keyframes: [{ opacity: 0, transform: 'translateY(-60px)' }, { opacity: 1, transform: 'translateY(0)' }] },
  slideInLeft: { name: 'slideInLeft', label: '左滑进入', group: 'in', keyframes: [{ opacity: 0, transform: 'translateX(-80px)' }, { opacity: 1, transform: 'translateX(0)' }] },
  slideInRight: { name: 'slideInRight', label: '右滑进入', group: 'in', keyframes: [{ opacity: 0, transform: 'translateX(80px)' }, { opacity: 1, transform: 'translateX(0)' }] },
  zoomIn: { name: 'zoomIn', label: '放大进入', group: 'in', keyframes: [{ opacity: 0, transform: 'scale(0.3)' }, { opacity: 1, transform: 'scale(1)' }] },
  bounceIn: { name: 'bounceIn', label: '弹入', group: 'in', keyframes: [
    { opacity: 0, transform: 'scale(0.3)', offset: 0 },
    { opacity: 1, transform: 'scale(1.1)', offset: 0.5 },
    { transform: 'scale(0.95)', offset: 0.7 },
    { opacity: 1, transform: 'scale(1)', offset: 1 },
  ] },
  flipInX: { name: 'flipInX', label: 'X 轴翻转进入', group: 'in', keyframes: [
    { opacity: 0, transform: 'perspective(400px) rotateX(90deg)', offset: 0 },
    { opacity: 1, transform: 'perspective(400px) rotateX(-20deg)', offset: 0.4 },
    { transform: 'perspective(400px) rotateX(10deg)', offset: 0.6 },
    { transform: 'perspective(400px) rotateX(-5deg)', offset: 0.8 },
    { opacity: 1, transform: 'perspective(400px) rotateX(0)', offset: 1 },
  ] },
  rotateIn: { name: 'rotateIn', label: '旋转进入', group: 'in', keyframes: [{ opacity: 0, transform: 'rotate(-200deg)' }, { opacity: 1, transform: 'rotate(0)' }] },
  pulse: { name: 'pulse', label: '脉冲', group: 'emphasis', keyframes: [{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }] },
  shake: { name: 'shake', label: '抖动', group: 'emphasis', keyframes: [
    { transform: 'translateX(0)' }, { transform: 'translateX(-8px)' }, { transform: 'translateX(8px)' },
    { transform: 'translateX(-8px)' }, { transform: 'translateX(8px)' }, { transform: 'translateX(0)' },
  ] },
  swing: { name: 'swing', label: '摇摆', group: 'emphasis', keyframes: [
    { transform: 'rotate(0)' }, { transform: 'rotate(12deg)' }, { transform: 'rotate(-9deg)' },
    { transform: 'rotate(6deg)' }, { transform: 'rotate(-3deg)' }, { transform: 'rotate(0)' },
  ] },
  flash: { name: 'flash', label: '闪烁', group: 'emphasis', keyframes: [{ opacity: 1 }, { opacity: 0 }, { opacity: 1 }, { opacity: 0 }, { opacity: 1 }] },
  fadeOut: { name: 'fadeOut', label: '淡出', group: 'out', keyframes: [{ opacity: 1 }, { opacity: 0 }] },
  slideOutDown: { name: 'slideOutDown', label: '下滑退出', group: 'out', keyframes: [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(60px)' }] },
  zoomOut: { name: 'zoomOut', label: '缩小退出', group: 'out', keyframes: [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.3)' }] },
};

export const PRESET_LIST = Object.values(ANIMATION_PRESETS);
export function getPreset(name: string): AnimationPreset | undefined {
  return ANIMATION_PRESETS[name];
}
